/**
 * Sport IQ — Single Course Module (day) reader.
 * Renders title, reading (long-form), 3 key takeaways, and links to every glossary term
 * referenced in this day so the student can drill down mid-lesson.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, ArrowRight, BookOpen, CheckCircle2, Volume2, Target, Lock, Zap, PlayCircle } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { getSportMeta } from '../lib/sportMeta';
import CoachBSpeak from '../components/CoachBSpeak';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const BasketballIQCourseModule = ({ sport = 'basketball' }) => {
  const meta = getSportMeta(sport);
  const ACCENT = meta.accent;
  const { slug } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [lockedInfo, setLockedInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [nextDay, setNextDay] = useState(null);
  const [buying, setBuying] = useState('');

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setLockedInfo(null);
    Promise.all([
      api.get(`/academy/${sport}-iq/module/${slug}`, { headers: authHeaders() }),
      api.get(`/academy/${sport}-iq/course`, { headers: authHeaders() }),
    ])
      .then(([m, c]) => {
        if (m.data.locked) {
          setLockedInfo({
            reason: m.data.reason,
            module: m.data.module,
            access: m.data.access,
            pricing: m.data.pricing,
          });
          setModule(null);
        } else {
          setModule(m.data.module);
          const allDays = (c.data.weeks || []).flatMap((w) => w.days.map((d) => ({ ...d, week: w.week, locked: w.locked })));
          const idx = allDays.findIndex((d) => d.slug === slug);
          // Only surface an unlocked next-day
          for (let j = idx + 1; j < allDays.length; j++) {
            if (!allDays[j].locked) { setNextDay(allDays[j]); break; }
          }
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [sport, slug]);

  const startCheckout = async (mode) => {
    setBuying(mode);
    try {
      const r = await api.post(`/academy/sport-iq/${sport}/checkout`,
        { mode, origin_url: window.location.origin },
        { headers: authHeaders() });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally { setBuying(''); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050508] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: ACCENT }} />
    </div>
  );

  // ─── Paywall block ──────────────────────────────────────────────────────
  if (lockedInfo) {
    const mod = lockedInfo.module || {};
    const price = lockedInfo.pricing || { drip_monthly: 79, full_onetime: 249 };
    const isWeekLocked = lockedInfo.reason === 'week_locked';
    return (
      <div className="min-h-screen bg-[#050508] text-white pt-16 pb-24" data-testid={`${sport}-iq-module-paywall`}>
        <div className="border-b-2 border-zinc-900 py-10 px-6" style={{ background: `linear-gradient(to bottom, ${ACCENT}0A, transparent)` }}>
          <div className="container mx-auto max-w-3xl">
            <Link to={`/${sport}-iq/course`} className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4">
              <ArrowLeft className="w-3 h-3" /> 6-Week Course
            </Link>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>
              <Lock className="w-3 h-3" /> {isWeekLocked ? 'Week Locked · Drip Cadence' : 'Locked Lesson'}
            </div>
            <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.05] mt-2">
              {mod.title || 'This lesson is behind the paywall'}
            </h1>
            <p className="text-zinc-300 text-base md:text-lg mt-4 leading-relaxed">
              {isWeekLocked
                ? `You're on drip access — this week unlocks 7 days at a time. Upgrade to Full Access ($${price.full_onetime}) to open every week right now.`
                : `Unlock ${meta.hub_title} to open this lesson and every module in the course.`}
            </p>
          </div>
        </div>

        <div className="container mx-auto max-w-3xl px-6 py-10">
          <div className="grid md:grid-cols-2 gap-3">
            <button
              onClick={() => startCheckout('full')}
              disabled={buying === 'full'}
              className="flex flex-col items-start gap-1 px-6 py-5 text-black text-left disabled:opacity-60"
              style={{ backgroundColor: ACCENT }}
              data-testid="paywall-module-full"
            >
              {buying === 'full' ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  <span className="text-[10px] font-black uppercase tracking-widest opacity-80">One-Time · Full Access</span>
                  <span className="text-3xl font-black leading-none mt-0.5">${price.full_onetime}</span>
                  <span className="text-[11px] font-bold uppercase tracking-wider mt-1">All 12 weeks unlocked · Skip around</span>
                </>
              )}
            </button>
            {!isWeekLocked && (
              <button
                onClick={() => startCheckout('drip')}
                disabled={buying === 'drip'}
                className="flex flex-col items-start gap-1 border-2 px-6 py-5 text-left hover:bg-white/5 disabled:opacity-60"
                style={{ borderColor: ACCENT, color: ACCENT }}
                data-testid="paywall-module-drip"
              >
                {buying === 'drip' ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Monthly · Drip Access</span>
                    <span className="text-3xl font-black leading-none mt-0.5">${price.drip_monthly}<span className="text-base opacity-80">/mo</span></span>
                    <span className="text-[11px] font-bold uppercase tracking-wider mt-1">1 week / 7 days · Cancel anytime</span>
                  </>
                )}
              </button>
            )}
          </div>
          <button
            onClick={() => navigate(`/${sport}-iq/glossary`)}
            className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-white mt-6"
            data-testid="paywall-module-glossary"
          >
            <Zap className="w-3 h-3" /> Free Preview: Browse the Full Glossary
          </button>
        </div>
      </div>
    );
  }

  if (notFound || !module) return (
    <div className="min-h-screen bg-[#050508] text-white pt-20 pb-24 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-black uppercase text-white mb-2">Lesson not found</h2>
        <Link to={`/${sport}-iq/course`} className="text-[11px] font-black uppercase tracking-widest" style={{ color: ACCENT }}>← Back to course</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050508] text-white pt-16 pb-24" data-testid={`${sport}-iq-course-module`}>
      {/* Header */}
      <div className="border-b-2 border-zinc-900 py-10 px-6" style={{ background: `linear-gradient(to bottom, ${ACCENT}0A, transparent)` }}>
        <div className="container mx-auto max-w-3xl">
          <Link to={`/${sport}-iq/course`} className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4" data-testid="back-to-course">
            <ArrowLeft className="w-3 h-3" /> 6-Week Course
          </Link>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>
            <Target className="w-3 h-3" /> Week {module.week} · Day {module.day}
          </div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.05] mt-2" data-testid="module-title">
            {module.title}
          </h1>
          <p className="text-zinc-300 text-base md:text-lg mt-4 leading-relaxed">
            {module.summary}
          </p>
        </div>
      </div>

      <div className="container mx-auto max-w-3xl px-6 py-10 space-y-8">
        {/* Voice narration — Coach B ElevenLabs */}
        <div className="mb-6 flex items-center gap-3" data-testid="module-coach-b-voice">
          <CoachBSpeak
            text={`This is Coach B. ${(module?.title || 'This module').replace(/\s+/g,' ')}. ${(module?.summary || module?.reading?.slice(0, 240) || '')}`.slice(0, 480)}
            label="Hear Coach B teach this"
            variant="full"
            accent={ACCENT}
            testId="module-coach-b"
          />
          <span className="text-[10px] text-gray-500 uppercase tracking-widest">Voice narration</span>
        </div>
        {module.voice_script && (
          <div className="border-2 border-zinc-800 bg-zinc-950 p-4 flex items-center gap-3" data-testid="module-voice">
            {module.audio_url ? (
              <>
                <Volume2 className="w-4 h-4 flex-shrink-0" style={{ color: ACCENT }} />
                <audio src={module.audio_url} controls className="flex-1" />
              </>
            ) : (
              <>
                <CoachBSpeak
                  text={module.voice_script.slice(0, 480)}
                  label="Play Coach B script"
                  variant="compact"
                  accent={ACCENT}
                  testId="module-voice-live"
                />
              </>
            )}
          </div>
        )}

        {/* Watch — YouTube clips picked automatically from the module's key terms.
            Silent no-op if YOUTUBE_API_KEY is missing in this environment. */}
        {module.videos?.length > 0 && (
          <div data-testid="module-videos">
            <div className="flex items-center gap-2 mb-4">
              <PlayCircle className="w-4 h-4" style={{ color: ACCENT }} />
              <span className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>Watch — See the Concept</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {module.videos.slice(0, 2).map((v, i) => (
                <div key={v.video_id || i} className="border-2 border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={`module-video-${i}`}>
                  <div className="aspect-video w-full bg-black">
                    <iframe
                      src={v.embed_url}
                      title={v.title || `Video ${i + 1}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3">
                    <div className="text-[13px] font-black text-white leading-snug line-clamp-2">{v.title}</div>
                    {v.channel && <div className="text-[10px] tracking-widest uppercase text-zinc-500 mt-1">{v.channel}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reading */}
        {module.reading && (
          <div data-testid="module-reading">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-4 h-4" style={{ color: ACCENT }} />
              <span className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>The Reading</span>
            </div>
            <div className="prose prose-invert prose-zinc max-w-none text-zinc-200 leading-relaxed space-y-4">
              {module.reading.split('\n\n').map((para, i) => (
                <p key={i} className="text-[15px] leading-[1.75]">
                  {para.split('**').map((chunk, j) =>
                    j % 2 === 1
                      ? <strong key={j} className="text-white font-black">{chunk}</strong>
                      : <span key={j}>{chunk}</span>
                  )}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Key Takeaways */}
        {module.key_takeaways?.length > 0 && (
          <div className="border-2 p-6" style={{ borderColor: `${ACCENT}44`, backgroundColor: `${ACCENT}0A` }} data-testid="module-takeaways">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-4 h-4" style={{ color: ACCENT }} />
              <span className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>Key Takeaways</span>
            </div>
            <ul className="space-y-3">
              {module.key_takeaways.map((t, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-6 h-6 border-2 flex items-center justify-center text-[11px] font-black" style={{ borderColor: ACCENT, color: ACCENT }}>
                    {i + 1}
                  </span>
                  <span className="text-sm text-white font-bold leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Examples — real-world basketball moments that illustrate the concept */}
        {module.examples?.length > 0 && (
          <div className="border-2 p-6 space-y-4" style={{ borderColor: '#22d3ee66', backgroundColor: '#22d3ee0A' }} data-testid="module-examples">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-cyan-400" />
              <span className="text-[10px] tracking-[0.4em] uppercase font-black text-cyan-400">See It In Action</span>
            </div>
            {module.examples.map((ex, i) => (
              <div key={i} className="border-l-2 border-cyan-400 pl-4">
                <div className="text-[13px] font-black text-white uppercase tracking-wide">{ex.title}</div>
                <div className="text-[13px] text-zinc-200 mt-1 leading-relaxed">{ex.what}</div>
                {ex.why_it_matters && (
                  <div className="text-[12px] text-cyan-300 mt-1 leading-relaxed italic">Why it matters: {ex.why_it_matters}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Day's Quiz — 3 MCQs with reveal explanations */}
        {module.quiz?.length > 0 && <ModuleQuiz questions={module.quiz} />}


        {module.terms?.length > 0 && (
          <div data-testid="module-terms">
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-4">
              Drill Deeper — Every Term in Today's Lesson
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {module.terms.map((t) => (
                <Link
                  key={t.slug}
                  to={`/${sport}-iq/term/${t.slug}`}
                  className="group border border-zinc-800 bg-zinc-950 p-4 hover:border-orange-500 transition-all"
                  data-testid={`linked-term-${t.slug}`}
                >
                  <div className="text-[9px] tracking-[0.3em] uppercase font-black text-zinc-500">{t.category?.replace(/-/g, ' ')}</div>
                  <div className="font-black text-sm text-white mt-1 flex items-center justify-between">
                    {t.term}
                    <ArrowRight className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100" />
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-1 line-clamp-2">{t.one_line}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Next day */}
        {nextDay && (
          <div className="pt-6 border-t border-zinc-900">
            <Link
              to={`/${sport}-iq/course/${nextDay.slug}`}
              className="group border-2 border-zinc-800 bg-zinc-950 hover:border-orange-500 transition-all p-5 flex items-center gap-4"
              data-testid="next-day"
              style={{ borderRightColor: ACCENT, borderRightWidth: 4 }}
            >
              <div className="flex-1">
                <div className="text-[9px] tracking-[0.3em] uppercase font-black text-zinc-500">Up Next · Week {nextDay.week} · Day {nextDay.day}</div>
                <div className="font-black text-white text-base mt-1">{nextDay.title}</div>
              </div>
              <ArrowRight className="w-6 h-6 flex-shrink-0" style={{ color: ACCENT }} />
            </Link>
          </div>
        )}

        {/* Attribution */}
        <div className="pt-6 text-center">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-600">
            Created by Coach B · Scholar Athlete Academy
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasketballIQCourseModule;

// ────────────────────────────────────────────────────────────────────────
// Inline quiz — 3-MCQ per module. Click to answer, reveal explanation,
// keep a running score. Purely local state; no persistence needed.
// ────────────────────────────────────────────────────────────────────────
const ModuleQuiz = ({ questions }) => {
  const [answers, setAnswers] = useState({}); // { [qIdx]: pickedIdx }
  const total = questions.length;
  const correct = questions.reduce((n, q, i) => n + (answers[i] === q.answer ? 1 : 0), 0);
  const answeredCount = Object.keys(answers).length;
  const done = answeredCount === total;

  return (
    <div className="border-2 border-yellow-500/60 bg-yellow-500/[0.04] p-6 space-y-5" data-testid="module-quiz">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-yellow-400" />
          <span className="text-[10px] tracking-[0.4em] uppercase font-black text-yellow-400">Day's Quiz · {total} Questions</span>
        </div>
        <div className="text-[10px] font-black uppercase tracking-widest text-yellow-300" data-testid="quiz-score">
          Score · {correct} / {total}
        </div>
      </div>
      {questions.map((q, i) => {
        const picked = answers[i];
        const isAnswered = picked !== undefined;
        return (
          <div key={i} className="border-l-2 border-yellow-500/50 pl-4 space-y-2" data-testid={`quiz-q-${i}`}>
            <div className="text-sm font-black text-white leading-snug">{i + 1}. {q.q || q.question}</div>
            <div className="grid gap-2 mt-2">
              {(q.options || []).map((opt, oi) => {
                const isPicked = picked === oi;
                const isCorrect = oi === q.answer;
                let border = 'border-zinc-800';
                let bg = 'bg-zinc-950';
                let txt = 'text-zinc-300';
                if (isAnswered) {
                  if (isCorrect) { border = 'border-emerald-500'; bg = 'bg-emerald-500/10'; txt = 'text-emerald-100'; }
                  else if (isPicked) { border = 'border-red-500'; bg = 'bg-red-500/10'; txt = 'text-red-200'; }
                  else { border = 'border-zinc-800'; bg = 'bg-zinc-950'; txt = 'text-zinc-500'; }
                }
                return (
                  <button
                    key={oi}
                    onClick={() => !isAnswered && setAnswers((a) => ({ ...a, [i]: oi }))}
                    disabled={isAnswered}
                    className={`text-left border-2 ${border} ${bg} ${txt} px-3 py-2 text-[13px] transition-colors hover:border-yellow-400 disabled:cursor-default`}
                    data-testid={`quiz-q-${i}-opt-${oi}`}
                  >
                    <span className="font-black mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                  </button>
                );
              })}
            </div>
            {isAnswered && q.explanation && (
              <div className="text-[12px] text-yellow-100/90 italic leading-relaxed mt-2 pl-1" data-testid={`quiz-q-${i}-explain`}>
                <span className="text-yellow-400 font-black not-italic">Why:</span> {q.explanation}
              </div>
            )}
          </div>
        );
      })}
      {done && (
        <div className="border-t border-yellow-500/30 pt-4 mt-2 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span className="text-[11px] font-black uppercase tracking-widest text-emerald-300" data-testid="quiz-complete">
            Quiz complete — {correct === total ? 'perfect score!' : `${correct} of ${total} correct.`}
          </span>
        </div>
      )}
    </div>
  );
};
